import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Read the currently-stored JWT (same source axios uses).
 * Exported so file/binary endpoints navigated to via ``<a href>`` /
 * ``<img src>`` / ``window.open`` — which cannot carry a header — can
 * append it as ``?t=<token>``. Backend `_api_auth_gate` accepts it.
 */
export function authToken() {
  try {
    return (typeof window !== "undefined")
      ? (window.localStorage?.getItem("od360_token") || "")
      : "";
  } catch { return ""; }
}

/** Suffix ``?t=<jwt>`` (or ``&t=<jwt>``) onto a URL so the browser carries
 *  auth on direct navigation. No-op when no token is stored. */
export function withAuthQuery(url) {
  const t = authToken();
  if (!t) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}t=${encodeURIComponent(t)}`;
}

const api = axios.create({ baseURL: API });

// Attach JWT Bearer + legacy `X-User-Id` (for backend audit fallback) on every call.
api.interceptors.request.use((config) => {
  try {
    if (typeof window !== "undefined") {
      const token = window.localStorage?.getItem("od360_token");
      const uid = window.localStorage?.getItem("od360_active_user_id");
      config.headers = config.headers || {};
      if (token) config.headers["Authorization"] = `Bearer ${token}`;
      if (uid) config.headers["X-User-Id"] = uid;
    }
  } catch { /* localStorage may be unavailable */ }
  return config;
});

// On 401 broadcast an "auth-expired" event so AppContext can clear state and
// the router can redirect to /login. Skip for the /auth/login endpoint itself —
// the login form needs to render the error inline.
api.interceptors.response.use(
  (r) => r,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !url.includes("/auth/login") && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("od360-auth-expired"));
    }
    return Promise.reject(err);
  },
);

/**
 * Render any axios error / FastAPI `detail` into a plain readable string —
 * never an object. FastAPI 422 `detail` is a list of validation dicts
 * ({type, loc, msg, …}); passing that array straight into `toast.error()` or
 * JSX would crash React with "Objects are not valid as a React child".
 */
export function apiErrorMessage(err, fallback = "Something went wrong") {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === "string") return detail.join(", ");
    if (first && typeof first === "object") {
      const loc = Array.isArray(first.loc) ? first.loc.filter((p) => p !== "body").join(".") : "";
      return loc ? `${loc}: ${first.msg || first.type}` : (first.msg || first.type || fallback);
    }
  }
  if (detail && typeof detail === "object") {
    return detail.msg || JSON.stringify(detail);
  }
  return err?.message || fallback;
}

export const Locations = {
  list: () => api.get("/locations").then((r) => r.data),
  create: (data) => api.post("/locations", data).then((r) => r.data),
  update: (id, data) => api.patch(`/locations/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/locations/${id}`).then((r) => r.data),
};

export const Masters = {
  list: (params) => api.get("/masters", { params }).then((r) => r.data),
  create: (data) => api.post("/masters", data).then((r) => r.data),
  update: (id, data) => api.patch(`/masters/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/masters/${id}`).then((r) => r.data),
};

export const Users = {
  list: (params) => api.get("/users", { params }).then((r) => r.data),
  create: (data) => api.post("/users", data).then((r) => r.data),
  update: (id, data) => api.patch(`/users/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/users/${id}`).then((r) => r.data),
  resetPassword: (id) => api.post(`/users/${id}/reset_password`).then((r) => r.data),
  deactivate: (id) => api.post(`/users/${id}/deactivate`).then((r) => r.data),
  activate: (id) => api.post(`/users/${id}/activate`).then((r) => r.data),
};

export const Auth = {
  login: (data) => api.post("/auth/login", data).then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (data) => api.post("/auth/change_password", data).then((r) => r.data),
};

export const Manager = {
  teamPerformance: (managerId, params) =>
    api.get(`/manager/${managerId}/team_performance`, { params }).then((r) => r.data),
};

// Date-range helpers — keeps the frontend consistent with backend `_date_range_query`.
// IMPORTANT: never use `.toISOString().slice(0,10)` on a local-midnight Date —
// in IST that shifts back 5:30h and returns the *previous* day. Format Y-M-D
// from local year/month/date components instead. (BUG-DATE-TZ, Feb 2026.)
const _pad = (n) => String(n).padStart(2, "0");
const _localYMD = (d) => `${d.getFullYear()}-${_pad(d.getMonth() + 1)}-${_pad(d.getDate())}`;

export const DateRanges = {
  thisMonth() {
    const now = new Date();
    const from = _localYMD(new Date(now.getFullYear(), now.getMonth(), 1));
    const to = _localYMD(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return { date_from: from, date_to: to };
  },
  today() {
    const s = _localYMD(new Date());
    return { date_from: s, date_to: s };
  },
  thisWeek() {
    const now = new Date();
    const day = now.getDay() || 7;            // Mon=1, Sun=7
    const monday = new Date(now); monday.setDate(now.getDate() - (day - 1));
    return {
      date_from: _localYMD(monday),
      date_to: _localYMD(new Date()),
    };
  },
  allTime() { return { date_from: undefined, date_to: undefined }; },
};

export const Sales = {
  list: (params) => api.get("/sales", { params }).then((r) => r.data),
  byIds: (ids) => api.post("/sales/by_ids", { ids }).then((r) => r.data),
  get: (id) => api.get(`/sales/${id}`).then((r) => r.data),
  lockState: (id) => api.get(`/sales/${id}/lock_state`).then((r) => r.data),
  create: (data) => api.post("/sales", data).then((r) => r.data),
  update: (id, data) => api.patch(`/sales/${id}`, data).then((r) => r.data),
  submit: (id) => api.post(`/sales/${id}/submit`).then((r) => r.data),
  deliver: (id, checklist) => api.post(`/sales/${id}/deliver`, checklist).then((r) => r.data),
  remove: (id) => api.delete(`/sales/${id}`).then((r) => r.data),
};

export const Pdi = {
  release: (saleId, data) =>
    api.post(`/sales/${saleId}/pdi/release_for_processing`, data).then((r) => r.data),
  setCheckSheet: (saleId, done) =>
    api.patch(`/sales/${saleId}/pdi/check_sheet`, { pdi_check_sheet_done: done }).then((r) => r.data),
  markReady: (saleId, data = {}) =>
    api.post(`/sales/${saleId}/pdi/ready`, data).then((r) => r.data),
};

export const Accounts = {
  stats: (params) => api.get("/accounts/stats", { params }).then((r) => r.data),
  saveDraft: (saleId, data) =>
    api.post(`/sales/${saleId}/accounts/save_draft`, data).then((r) => r.data),
  complete: (saleId, data) =>
    api.post(`/sales/${saleId}/accounts/complete`, data).then((r) => r.data),
  releaseTransit: (saleId, data) =>
    api.post(`/sales/${saleId}/transit/release`, data).then((r) => r.data),
  updateBillingLedger: (saleId, data) =>
    api.patch(`/sales/${saleId}/accounts/billing_ledger`, data).then((r) => r.data),
};

export const BackOffice = {
  stats: (params) => api.get("/back_office/stats", { params }).then((r) => r.data),
  insuranceComplete: (saleId, data) =>
    api.post(`/sales/${saleId}/insurance/complete`, data).then((r) => r.data),
  roadTaxComplete: (saleId, data) =>
    api.post(`/sales/${saleId}/road_tax/complete`, data).then((r) => r.data),
  roadTaxSubmitToRto: (saleId, data) =>
    api.post(`/sales/${saleId}/road_tax/submit_to_rto`, data).then((r) => r.data),
  hsrpUpdate: (saleId, data) =>
    api.post(`/sales/${saleId}/hsrp/update`, data).then((r) => r.data),
  vasSaveDraft: (saleId, kind, data) =>
    api.post(`/sales/${saleId}/vas/${kind}/save_draft`, data).then((r) => r.data),
  vasComplete: (saleId, kind, data) =>
    api.post(`/sales/${saleId}/vas/${kind}/complete`, data).then((r) => r.data),
};

export const Spares = {
  stats: (params) => api.get("/spares/stats", { params }).then((r) => r.data),
  saveDraft: (saleId, data) =>
    api.post(`/sales/${saleId}/spares/save_draft`, data).then((r) => r.data),
  complete: (saleId, data) =>
    api.post(`/sales/${saleId}/spares/complete`, data).then((r) => r.data),
};

export const Tasks = {
  list: (params) => api.get("/tasks", { params }).then((r) => r.data),
  complete: (id, data = {}) =>
    api.post(`/tasks/${id}/complete`, data).then((r) => r.data),
  markDelay: (id, data) =>
    api.post(`/tasks/${id}/mark_delay`, data).then((r) => r.data),
  clearDelay: (id) =>
    api.delete(`/tasks/${id}/mark_delay`).then((r) => r.data),
  slaPause: (id, data) =>
    api.post(`/tasks/${id}/sla_pause`, data).then((r) => r.data),
  slaResume: (id) =>
    api.post(`/tasks/${id}/sla_resume`).then((r) => r.data),
};

export const WorkingHours = {
  list: () => api.get("/working_hours").then((r) => r.data),
  upsert: (data) => api.put("/working_hours", data).then((r) => r.data),
};

export const Holidays = {
  list: (params) => api.get("/holidays", { params }).then((r) => r.data),
  create: (data) => api.post("/holidays", data).then((r) => r.data),
  remove: (id) => api.delete(`/holidays/${id}`).then((r) => r.data),
};

export const SlaMasters = {
  list: (params) => api.get("/sla_masters", { params }).then((r) => r.data),
  meta: () => api.get("/sla_masters/meta").then((r) => r.data),
  create: (data) => api.post("/sla_masters", data).then((r) => r.data),
  update: (id, data) => api.put(`/sla_masters/${id}`, data).then((r) => r.data),
  remove: (id) => api.delete(`/sla_masters/${id}`).then((r) => r.data),
};

export const Sla = {
  dashboard: (params) => api.get("/sla/dashboard", { params }).then((r) => r.data),
};

export const AuditLogs = {
  list: (params) => api.get("/audit_logs", { params }).then((r) => r.data),
};

export const Files = {
  upload: (saleId, type, file, uploadedBy) => {
    const fd = new FormData();
    fd.append("type", type);
    fd.append("file", file);
    if (uploadedBy) fd.append("uploaded_by", uploadedBy);
    return api.post(`/sales/${saleId}/files`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  list: (saleId, type) =>
    api.get(`/sales/${saleId}/files`, { params: type ? { type } : {} }).then((r) => r.data),
  remove: (id) => api.delete(`/files/${id}`).then((r) => r.data),
  // Direct browser navigation (<a href>, <img src>, window.open) can't inject
  // an Authorization header. Append ?t=<jwt> so the router auth gate lets it
  // through with the same JWT the axios interceptor uses.
  url: (id) => withAuthQuery(`${API}/files/${id}`),
};

export const Delivery = {
  list: (params) => api.get("/delivery_files", { params }).then((r) => r.data),
  get: (saleId) => api.get(`/sales/${saleId}/delivery_file`).then((r) => r.data),
  preflight: (saleId) => api.get(`/sales/${saleId}/delivery_file/preflight`).then((r) => r.data),
  // PDF is binary — we request a blob so the browser can render / download it.
  pdf: (saleId) =>
    api.get(`/sales/${saleId}/delivery_file.pdf`, { responseType: "blob" })
      .then((r) => r.data),
  pdfUrl: (saleId) => withAuthQuery(`${API}/sales/${saleId}/delivery_file.pdf`),
  checklist: (saleId) => api.get(`/sales/${saleId}/delivery_checklist`).then((r) => r.data),
};

export const Receipts = {
  list: (saleId) => api.get(`/sales/${saleId}/payment_receipts`).then((r) => r.data),
  save: (saleId, receipts, userId) =>
    api.put(`/sales/${saleId}/payment_receipts`, { receipts, user_id: userId }).then((r) => r.data),
  add: (saleId, payload) =>
    api.post(`/sales/${saleId}/payment_receipts/add`, payload).then((r) => r.data),
};

export const Stats = {
  get: (params) => api.get("/stats", { params }).then((r) => r.data),
  counterDistrict: (params) =>
    api.get("/stats/counter_district", { params }).then((r) => r.data),
  departmentPendency: (params) =>
    api.get("/stats/department_pendency", { params }).then((r) => r.data),
  actionRequired: (params) =>
    api.get("/stats/action_required", { params }).then((r) => r.data),
  workflowSummary: (params) =>
    api.get("/stats/workflow_summary", { params }).then((r) => r.data),
};

export const Seed = {
  run: () => api.post("/seed").then((r) => r.data),
};

export const AdminOps = {
  factoryReset: (confirm) =>
    api.post("/admin/factory_reset", { confirm }).then((r) => r.data),
};

export const EarlyDelivery = {
  request: (saleId, data) =>
    api.post(`/sales/${saleId}/early_delivery/request`, data).then((r) => r.data),
  approve: (saleId, data = {}) =>
    api.post(`/sales/${saleId}/early_delivery/approve`, data).then((r) => r.data),
  reject: (saleId, data) =>
    api.post(`/sales/${saleId}/early_delivery/reject`, data).then((r) => r.data),
  queue: (params) =>
    api.get("/early_delivery/queue", { params }).then((r) => r.data),
};

// AI Assistant (GPT 5.6 Sol via emergentintegrations). All calls are
// role-scoped on the server — the caller only sees their own sales data.
export const AI = {
  ask: (question) =>
    api.post("/ai/ask", { question }).then((r) => r.data),
  summarize: (saleId) =>
    api.post(`/ai/summarize/${saleId}`).then((r) => r.data),
  smInsights: () =>
    api.post("/ai/sm_insights").then((r) => r.data),
};

export default api;
